import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { rem } from 'polished';
import { Button, Box, Checkbox, Flex, Label, Modal as RooModal, Paragraph } from 'roo-ui/components';
import {
  CUSTOMER_SUPPORT_CALL_HOURS,
  CUSTOMER_SUPPORT_CALL_DAYS,
  CUSTOMER_SUPPORT_HOTLINE_AU,
  CUSTOMER_SUPPORT_HOTLINE_INTL,
} from 'config/constants';
import { useDetailsBooking } from 'lib/hooks';
import { getUpdatePaymentMethodPageLink } from 'lib/links';
import * as track from 'lib/gaTracking/bookingDetailsPageEvents';
import { showDepositPayAdyenMigrationInfo } from '../../../helpers';
import { Modal, ModalFooter } from './primitives';
import TelLink from 'components/TelLink';

const PaymentMethodUpdateRequiredModal: FC = () => {
  const { booking } = useDetailsBooking();
  const renderPaymentMethodUpdateModal = showDepositPayAdyenMigrationInfo(booking);
  const [displayPaymentUpdateModal, setDisplayPaymentUpdateModal] = useState(renderPaymentMethodUpdateModal);
  const [acknowledged, setAcknowledged] = useState(false);
  const toggleAcknowledged = () => setAcknowledged(!acknowledged);
  const updatePaymentMethodLink = getUpdatePaymentMethodPageLink(booking.id);
  const close = () => {
    track.updatePaymentDetailsModalDismissSelected();
    setDisplayPaymentUpdateModal(false);
  };

  if (!displayPaymentUpdateModal) {
    return null;
  }

  return (
    <Modal isOpen={displayPaymentUpdateModal} ariaHideApp={false}>
      <RooModal.header variant="error">
        <Paragraph textAlign="center">Action required! We are unable to process your upcoming payment</Paragraph>
      </RooModal.header>
      <RooModal.body pb={[rem(160), 0]} textAlign="left">
        <Paragraph pb={2}>
          Due to an upgrade to our payment system, we are no longer storing your credit card details. To ensure your final payment is
          processed and your booking is secured, you will need to re-enter your payment details. Payment details must be updated before any
          changes or cancellations can be made.
        </Paragraph>

        <Paragraph pb={8}>
          Please follow the link below to update your payment details or contact us. Our support team is available{' '}
          {CUSTOMER_SUPPORT_CALL_HOURS}, {CUSTOMER_SUPPORT_CALL_DAYS} on{' '}
          <TelLink
            data-testid="call-support-link"
            number={CUSTOMER_SUPPORT_HOTLINE_AU}
            color="greys.steel"
            underline
            onClick={() => track.updatePaymentDetailsModalLinkSelected('call customer support')}
          />
          , or outside Australia:{' '}
          <TelLink
            data-testid="call-international-support-link"
            number={CUSTOMER_SUPPORT_HOTLINE_INTL}
            color="greys.steel"
            underline
            onClick={() => track.updatePaymentDetailsModalLinkSelected('call international customer support')}
          />
        </Paragraph>

        <Flex flexDirection="row" justifyContent="space-between" pb={4}>
          <form>
            <Label mb={0}>
              <Flex flexDirection="row">
                <Box>
                  <Checkbox name="dismiss-payment-reminder" onChange={toggleAcknowledged} />
                </Box>
                <Paragraph pl={2} pt="2px">
                  I understand that I need to update my payment details for each booking to avoid automated cancellation
                </Paragraph>
              </Flex>
            </Label>
          </form>
        </Flex>
      </RooModal.body>
      <ModalFooter py={[4, 8]}>
        <Flex flexDirection={['column', 'row']} justifyContent="space-between" alignItems="stretch" width="100%">
          <Button
            variant="primary"
            as={Link}
            to={updatePaymentMethodLink}
            mb={[2, 0]}
            mr={[0, 6]}
            width={['100%', '65%']}
            onClick={track.updatePaymentDetailsModalUpdateSelected}
          >
            UPDATE PAYMENT DETAILS
          </Button>
          <Button disabled={!acknowledged} onClick={close} width={['100%', '35%']}>
            NO THANKS
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export default PaymentMethodUpdateRequiredModal;
